import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { fetchFeedAlbums } from "@/lib/feed-albums.server";
import { getPremiumUserIds } from "@/lib/premium.server";

export type FeedRow = {
  id: string;
  user_id: string;
  type: string;
  body: string | null;
  created_at: string;
  album: {
    id: string;
    discogs_id: string | null;
    title: string;
    artist: string;
    cover_url: string | null;
    year: number | null;
    status: string;
    price_brl: number | null;
  } | null;
  author: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    is_premium: boolean;
  };
  likes_count: number;
  comments_count: number;
  liked_by_me: boolean;
};

export type FeedPage = {
  rows: FeedRow[];
  nextOffset: number;
  hasMore: boolean;
};

function parseFeedPage(data: unknown): { offset: number; limit: number } {
  const d = (data ?? {}) as { offset?: unknown; limit?: unknown };
  const offset = Math.max(0, Number(d.offset ?? 0) || 0);
  const raw = Number(d.limit ?? 60) || 60;
  const limit = Math.min(60, Math.max(1, raw));
  return { offset, limit };
}

export const listFollowingFeed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FeedRow[]> => {
    const { supabase, userId } = context;
    const { data: follows } = await supabase
      .from("follows")
      .select("following_id")
      .eq("follower_id", userId);
    const ids = [userId, ...(follows ?? []).map((f) => f.following_id)];

    const { data: posts, error } = await supabase
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .in("user_id", ids)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    if (!posts || posts.length === 0) return [];

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const postIds = posts.map((p) => p.id);

    const [authors, albums, likes, myLikes, comments] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", authorIds),
      fetchFeedAlbums(albumIds),
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase
        .from("post_likes")
        .select("post_id")
        .in("post_id", postIds)
        .eq("user_id", userId),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
    ]);

    const authorMap = new Map(
      (authors.data ?? []).map((a) => [a.id, a]),
    );
    const albumMap = new Map(
      (albums.data ?? []).map((a: { id: string }) => [a.id, a]),
    );
    const likesByPost = new Map<string, number>();
    (likes.data ?? []).forEach((l) =>
      likesByPost.set(l.post_id, (likesByPost.get(l.post_id) ?? 0) + 1),
    );
    const commentsByPost = new Map<string, number>();
    (comments.data ?? []).forEach((c) =>
      commentsByPost.set(c.post_id, (commentsByPost.get(c.post_id) ?? 0) + 1),
    );
    const mineSet = new Set((myLikes.data ?? []).map((l) => l.post_id));

    const premiumIds = await getPremiumUserIds(authorIds);

    return posts.map((p) => {
      const author = authorMap.get(p.user_id);
      return {
        id: p.id,
        user_id: p.user_id,
        type: p.type,
        body: p.body,
        created_at: p.created_at,
        album: p.album_id ? (albumMap.get(p.album_id) as FeedRow["album"]) ?? null : null,
        author: author
          ? { ...author, is_premium: premiumIds.has(author.id) }
          : { id: p.user_id, username: "?", display_name: null, avatar_url: null, is_premium: false },
        likes_count: likesByPost.get(p.id) ?? 0,
        comments_count: commentsByPost.get(p.id) ?? 0,
        liked_by_me: mineSet.has(p.id),
      };
    });
  });

export const listWishlistInterestFeed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator(parseFeedPage)
  .handler(async ({ context, data }): Promise<FeedPage> => {
    const { supabase, userId } = context;
    const { offset, limit } = data;

    const { data: posts, error } = await supabase
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .eq("type", "added_to_wishlist")
      .not("album_id", "is", null)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);
    if (error) throw new Error(error.message);
    if (!posts || posts.length === 0)
      return { rows: [], nextOffset: offset, hasMore: false };

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const postIds = posts.map((p) => p.id);

    const [authors, albums, likes, myLikes, comments] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", authorIds),
      fetchFeedAlbums(albumIds),
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase
        .from("post_likes")
        .select("post_id")
        .in("post_id", postIds)
        .eq("user_id", userId),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
    ]);

    const authorMap = new Map((authors.data ?? []).map((a) => [a.id, a]));
    const albumMap = new Map(
      (albums.data ?? []).map((a: { id: string }) => [a.id, a]),
    );
    const likesByPost = new Map<string, number>();
    (likes.data ?? []).forEach((l) =>
      likesByPost.set(l.post_id, (likesByPost.get(l.post_id) ?? 0) + 1),
    );
    const commentsByPost = new Map<string, number>();
    (comments.data ?? []).forEach((c) =>
      commentsByPost.set(c.post_id, (commentsByPost.get(c.post_id) ?? 0) + 1),
    );
    const mineSet = new Set((myLikes.data ?? []).map((l) => l.post_id));
    const premiumIds = await getPremiumUserIds(authorIds);

    const rows: FeedRow[] = posts
      .map((p) => {
        const author = authorMap.get(p.user_id);
        const album = p.album_id
          ? (albumMap.get(p.album_id) as FeedRow["album"]) ?? null
          : null;
        if (!album?.cover_url) return null;
        return {
          id: p.id,
          user_id: p.user_id,
          type: p.type,
          body: p.body,
          created_at: p.created_at,
          album,
          author: author
            ? { ...author, is_premium: premiumIds.has(author.id) }
            : {
                id: p.user_id,
                username: "?",
                display_name: null,
                avatar_url: null,
                is_premium: false,
              },
          likes_count: likesByPost.get(p.id) ?? 0,
          comments_count: commentsByPost.get(p.id) ?? 0,
          liked_by_me: mineSet.has(p.id),
        } as FeedRow;
      })
      .filter((x): x is FeedRow => x !== null);

    const seen = new Set<string>();
    const deduped = rows.filter((r) => {
      const key = r.album!.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return {
      rows: deduped,
      nextOffset: offset + posts.length,
      hasMore: posts.length === limit,
    };
  });

export const listRecentCollectionFeed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator(parseFeedPage)
  .handler(async ({ context, data }): Promise<FeedPage> => {
    const { supabase, userId } = context;
    const { offset, limit } = data;

    const { data: posts, error } = await supabase
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .eq("type", "added_to_collection")
      .not("album_id", "is", null)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);
    if (error) throw new Error(error.message);
    if (!posts || posts.length === 0)
      return { rows: [], nextOffset: offset, hasMore: false };

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const postIds = posts.map((p) => p.id);

    const [authors, albums, likes, myLikes, comments] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", authorIds),
      fetchFeedAlbums(albumIds),
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase
        .from("post_likes")
        .select("post_id")
        .in("post_id", postIds)
        .eq("user_id", userId),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
    ]);

    const authorMap = new Map((authors.data ?? []).map((a) => [a.id, a]));
    const albumMap = new Map(
      (albums.data ?? []).map((a: { id: string }) => [a.id, a]),
    );
    const likesByPost = new Map<string, number>();
    (likes.data ?? []).forEach((l) =>
      likesByPost.set(l.post_id, (likesByPost.get(l.post_id) ?? 0) + 1),
    );
    const commentsByPost = new Map<string, number>();
    (comments.data ?? []).forEach((c) =>
      commentsByPost.set(c.post_id, (commentsByPost.get(c.post_id) ?? 0) + 1),
    );
    const mineSet = new Set((myLikes.data ?? []).map((l) => l.post_id));
    const premiumIds = await getPremiumUserIds(authorIds);

    const rows: FeedRow[] = posts
      .map((p) => {
        const author = authorMap.get(p.user_id);
        const album = p.album_id
          ? (albumMap.get(p.album_id) as FeedRow["album"]) ?? null
          : null;
        if (!album?.cover_url) return null;
        return {
          id: p.id,
          user_id: p.user_id,
          type: p.type,
          body: p.body,
          created_at: p.created_at,
          album,
          author: author
            ? { ...author, is_premium: premiumIds.has(author.id) }
            : {
                id: p.user_id,
                username: "?",
                display_name: null,
                avatar_url: null,
                is_premium: false,
              },
          likes_count: likesByPost.get(p.id) ?? 0,
          comments_count: commentsByPost.get(p.id) ?? 0,
          liked_by_me: mineSet.has(p.id),
        } as FeedRow;
      })
      .filter((x): x is FeedRow => x !== null);

    const seen = new Set<string>();
    const deduped = rows.filter((r) => {
      const key = r.album!.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return {
      rows: deduped,
      nextOffset: offset + posts.length,
      hasMore: posts.length === limit,
    };
  });

export const listSpotlightFeed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FeedRow[]> => {
    const { supabase, userId } = context;

    const { data: posts, error } = await supabase
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .not("album_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    if (!posts || posts.length === 0) return [];

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const postIds = posts.map((p) => p.id);

    const [authors, albums, likes, myLikes, comments] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", authorIds),
      fetchFeedAlbums(albumIds),
      supabase.from("post_likes").select("post_id").in("post_id", postIds),
      supabase
        .from("post_likes")
        .select("post_id")
        .in("post_id", postIds)
        .eq("user_id", userId),
      supabase.from("post_comments").select("post_id").in("post_id", postIds),
    ]);

    const authorMap = new Map((authors.data ?? []).map((a) => [a.id, a]));
    const albumMap = new Map(
      (albums.data ?? []).map((a: { id: string }) => [a.id, a]),
    );
    const likesByPost = new Map<string, number>();
    (likes.data ?? []).forEach((l) =>
      likesByPost.set(l.post_id, (likesByPost.get(l.post_id) ?? 0) + 1),
    );
    const commentsByPost = new Map<string, number>();
    (comments.data ?? []).forEach((c) =>
      commentsByPost.set(c.post_id, (commentsByPost.get(c.post_id) ?? 0) + 1),
    );
    const mineSet = new Set((myLikes.data ?? []).map((l) => l.post_id));
    const premiumIds = await getPremiumUserIds(authorIds);

    const rows: FeedRow[] = posts
      .map((p) => {
        const author = authorMap.get(p.user_id);
        const album = p.album_id
          ? (albumMap.get(p.album_id) as FeedRow["album"]) ?? null
          : null;
        if (!album?.cover_url) return null;
        return {
          id: p.id,
          user_id: p.user_id,
          type: p.type,
          body: p.body,
          created_at: p.created_at,
          album,
          author: author
            ? { ...author, is_premium: premiumIds.has(author.id) }
            : {
                id: p.user_id,
                username: "?",
                display_name: null,
                avatar_url: null,
                is_premium: false,
              },
          likes_count: likesByPost.get(p.id) ?? 0,
          comments_count: commentsByPost.get(p.id) ?? 0,
          liked_by_me: mineSet.has(p.id),
        } as FeedRow;
      })
      .filter((x): x is FeedRow => x !== null);

    // Dedup by album so the masonry shows distinct covers
    const seen = new Set<string>();
    return rows.filter((r) => {
      const key = r.album!.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });

export const togglePostLike = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { postId: string }) => i)
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase
      .from("post_likes")
      .select("post_id")
      .eq("post_id", data.postId)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (existing) {
      const { error } = await context.supabase
        .from("post_likes")
        .delete()
        .eq("post_id", data.postId)
        .eq("user_id", context.userId);
      if (error) throw new Error(error.message);
      return { liked: false };
    }
    const { error } = await context.supabase
      .from("post_likes")
      .insert({ post_id: data.postId, user_id: context.userId });
    if (error) throw new Error(error.message);
    return { liked: true };
  });

export const listPostComments = createServerFn({ method: "GET" })
  .inputValidator((i: { postId: string }) => i)
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false } },
    );
    const { data: comments } = await sb
      .from("post_comments")
      .select("id, body, created_at, edited_at, user_id, media_url, media_type")
      .eq("post_id", data.postId)
      .order("created_at", { ascending: true });
    if (!comments || comments.length === 0) return [];
    const userIds = [...new Set(comments.map((c) => c.user_id))];
    const { data: profiles } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", userIds);
    const premiumIds = await getPremiumUserIds(userIds);
    const pmap = new Map((profiles ?? []).map((p) => [p.id, p]));
    return comments.map((c) => ({
      ...c,
      author: pmap.get(c.user_id)
        ? {
            ...pmap.get(c.user_id)!,
            is_premium: premiumIds.has(c.user_id),
          }
        : null,
    }));
  });

export const updatePostComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { id: string; body: string }) => {
    const body = i.body.trim();
    if (body.length < 1 || body.length > 1000) throw new Error("Comentário inválido");
    return { id: i.id, body };
  })
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("post_comments")
      .update({ body: data.body, edited_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deletePostComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { id: string }) => i)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("post_comments")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { postId: string; body?: string; mediaUrl?: string; mediaType?: string }) => {
    const body = (i.body ?? "").trim();
    const mediaUrl = i.mediaUrl?.trim() || null;
    if (!body && !mediaUrl) throw new Error("Comentário inválido");
    if (body.length > 1000) throw new Error("Comentário muito longo");
    return { postId: i.postId, body, mediaUrl, mediaType: mediaUrl ? i.mediaType ?? "gif" : null };
  })
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any)
      .from("post_comments")
      .insert({
        post_id: data.postId,
        user_id: context.userId,
        body: data.body,
        media_url: data.mediaUrl,
        media_type: data.mediaType,
      });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type PostLiker = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_premium: boolean;
};

/** Lista pública de quem curtiu um post do feed (ordem: curtida mais recente). */
export const listPostLikers = createServerFn({ method: "GET" })
  .inputValidator((i: { postId: string }) => i)
  .handler(async ({ data }): Promise<PostLiker[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false } },
    );
    const { data: likes } = await sb
      .from("post_likes")
      .select("user_id, created_at")
      .eq("post_id", data.postId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (!likes || likes.length === 0) return [];
    const userIds = [...new Set(likes.map((l) => l.user_id))];
    const { data: profiles } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", userIds);
    const premiumIds = await getPremiumUserIds(userIds);
    const pmap = new Map((profiles ?? []).map((p) => [p.id, p]));
    return userIds
      .map((id) => {
        const p = pmap.get(id);
        if (!p) return null;
        return {
          id: p.id,
          username: p.username,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          is_premium: premiumIds.has(id),
        };
      })
      .filter((p): p is PostLiker => !!p);
  });

/** Versão pública (sem sessão) do feed de adicionados recentemente. */
export const listRecentCollectionFeedPublic = createServerFn({ method: "GET" }).handler(
  async (): Promise<FeedRow[]> => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false } },
    );
    const { data: posts } = await sb
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .eq("type", "added_to_collection")
      .not("album_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(60);
    if (!posts || posts.length === 0) return [];

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const postIds = posts.map((p) => p.id);

    const [authors, albums, likes, comments] = await Promise.all([
      sb.from("profiles").select("id, username, display_name, avatar_url").in("id", authorIds),
      fetchFeedAlbums(albumIds),
      sb.from("post_likes").select("post_id").in("post_id", postIds),
      sb.from("post_comments").select("post_id").in("post_id", postIds),
    ]);

    const authorMap = new Map((authors.data ?? []).map((a) => [a.id, a]));
    const albumMap = new Map((albums.data ?? []).map((a: { id: string }) => [a.id, a]));
    const likesByPost = new Map<string, number>();
    (likes.data ?? []).forEach((l) =>
      likesByPost.set(l.post_id, (likesByPost.get(l.post_id) ?? 0) + 1),
    );
    const commentsByPost = new Map<string, number>();
    (comments.data ?? []).forEach((c) =>
      commentsByPost.set(c.post_id, (commentsByPost.get(c.post_id) ?? 0) + 1),
    );
    const premiumIds = await getPremiumUserIds(authorIds);

    const rows = posts
      .map((p) => {
        const author = authorMap.get(p.user_id);
        const album = p.album_id ? (albumMap.get(p.album_id) as FeedRow["album"]) ?? null : null;
        if (!album?.cover_url) return null;
        return {
          id: p.id,
          user_id: p.user_id,
          type: p.type,
          body: p.body,
          created_at: p.created_at,
          album,
          author: author
            ? { ...author, is_premium: premiumIds.has(author.id) }
            : {
                id: p.user_id,
                username: "?",
                display_name: null,
                avatar_url: null,
                is_premium: false,
              },
          likes_count: likesByPost.get(p.id) ?? 0,
          comments_count: commentsByPost.get(p.id) ?? 0,
          liked_by_me: false,
        } as FeedRow;
      })
      .filter((x): x is FeedRow => x !== null);

    const seen = new Set<string>();
    return rows.filter((r) => {
      const key = r.album!.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  },
);

export type ActivityRow = {
  id: string;
  type: string;
  created_at: string;
  body: string | null;
  author: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    is_premium: boolean;
  };
  album: { id: string; title: string; artist: string; cover_url: string | null; discogs_id: string | null } | null;
};

/** Atividades recentes de toda a comunidade (para engajamento no Explorar). */
export const listFeedActivity = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ActivityRow[]> => {
    const { supabase } = context;
    const { data: posts, error } = await supabase
      .from("feed_posts")
      .select("id, user_id, type, body, created_at, album_id")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);
    if (!posts || posts.length === 0) return [];

    const authorIds = [...new Set(posts.map((p) => p.user_id))];
    const albumIds = posts.map((p) => p.album_id).filter(Boolean) as string[];
    const [authors, albums, premiumIds] = await Promise.all([
      supabase.from("profiles").select("id, username, display_name, avatar_url").in("id", authorIds),
      fetchFeedAlbums(albumIds),
      getPremiumUserIds(authorIds),
    ]);
    const authorMap = new Map((authors.data ?? []).map((a) => [a.id, a]));
    const albumMap = new Map((albums.data ?? []).map((a: { id: string }) => [a.id, a]));

    return posts.map((p) => {
      const a = authorMap.get(p.user_id);
      const album = p.album_id
        ? ((albumMap.get(p.album_id) as ActivityRow["album"]) ?? null)
        : null;
      return {
        id: p.id,
        type: p.type,
        created_at: p.created_at,
        body: p.body,
        author: a
          ? { ...a, is_premium: premiumIds.has(a.id) }
          : { id: p.user_id, username: "?", display_name: null, avatar_url: null, is_premium: false },
        album,
      } as ActivityRow;
    });
  });
